import * as THREE from "three";
import { ShaderPass } from "three/addons/postprocessing/ShaderPass.js";

const noiseGLSL = `
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1,311.7))) * 43758.5453123); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p); f = f*f*(3.0-2.0*f);
  return mix(mix(hash(i), hash(i+vec2(1,0)), f.x), mix(hash(i+vec2(0,1)), hash(i+vec2(1,1)), f.x), f.y);
}
float fbm(vec2 p) { return noise(p)*0.55 + noise(p*2.03)*0.3 + noise(p*4.01)*0.15; }
`;

/** White painted plaster: static micro-bump, diffuse light, no mirror pass. */
export function createMatteFloor() {
  const geometry = new THREE.PlaneGeometry(180, 220);
  const texels = new Uint8Array(128 * 128);
  let seed = 71419;
  for (let i = 0; i < texels.length; i++) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    texels[i] = 96 + Math.floor((seed / 4294967296) * 64);
  }
  const bump = new THREE.DataTexture(texels, 128, 128, THREE.RedFormat);
  bump.wrapS = bump.wrapT = THREE.RepeatWrapping;
  bump.magFilter = THREE.LinearFilter;
  bump.minFilter = THREE.LinearMipmapLinearFilter;
  bump.generateMipmaps = true;
  bump.repeat.set(90, 110);
  bump.needsUpdate = true;
  const material = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 1,
    metalness: 0,
    envMapIntensity: 0,
    bumpMap: bump,
    bumpScale: 0.012,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.set(0, -3.7, -60);
  return { mesh, dispose() { geometry.dispose(); material.dispose(); bump.dispose(); } };
}

export function createMist() {
  const group = new THREE.Group();
  const geometry = new THREE.PlaneGeometry(60, 120);
  const material = new THREE.ShaderMaterial({
    transparent: true, depthWrite: false, side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
    uniforms: { time: { value: 0 }, light: { value: 0 } },
    vertexShader: `
      varying vec3 vWorld; varying vec2 vUv;
      void main() { vUv=uv; vWorld=(modelMatrix*vec4(position,1.0)).xyz;
        gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }`,
    fragmentShader: `
      varying vec3 vWorld; varying vec2 vUv; uniform float time; uniform float light;
      ${noiseGLSL}
      void main() {
        float wisps=fbm(vWorld.xz*vec2(0.13,0.07)+vec2(time*0.018,-time*0.025));
        wisps *= fbm(vWorld.xz*vec2(0.06,0.16)-time*0.012);
        float edge=smoothstep(0.0,0.14,vUv.x)*(1.0-smoothstep(0.86,1.0,vUv.x))
          *smoothstep(0.0,0.12,vUv.y)*(1.0-smoothstep(0.86,1.0,vUv.y));
        gl_FragColor=vec4(vec3(0.3,0.33,0.36),wisps*edge*light*0.025);
      }`,
  });
  for (let i = 0; i < 4; i++) {
    const sheet = new THREE.Mesh(geometry, material);
    sheet.rotation.x = -Math.PI / 2;
    sheet.position.set(i % 2 ? -2 : 2, -3.3 + i * 0.55, -44);
    group.add(sheet);
  }
  return { group, geometry, material };
}

/** Fixed-amplitude, independent per-frame noise applied after tone mapping. */
export function createFilmGrain() {
  const pass = new ShaderPass({
    uniforms: { tDiffuse: { value: null }, seed: { value: new THREE.Vector2() }, amount: { value: 0.035 } },
    vertexShader: `varying vec2 vUv; void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`,
    fragmentShader: `
      uniform sampler2D tDiffuse; uniform vec2 seed; uniform float amount; varying vec2 vUv;
      float noise(vec2 cell) {
        vec3 p=fract(vec3(cell.x,cell.y,cell.x)*0.1031);
        p+=dot(p,p.yzx+33.33);
        return fract((p.x+p.y)*p.z);
      }
      void main(){
        vec3 color=texture2D(tDiffuse,vUv).rgb;
        float grain=noise(floor(gl_FragCoord.xy)+seed)-0.5;
        gl_FragColor=vec4(clamp(color+grain*amount,0.0,1.0),1.0);
      }`,
  });
  pass.material.depthTest = false;
  pass.material.depthWrite = false;
  return pass;
}
